import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subscription } from 'rxjs'
import { AramexSettings, AramexSettingsService } from './aramex-settings.service'
import { getCityOptions, getCountryOptions, MAJOR_SA_CITIES } from '../shared/origin-location.util'

const ARAMEX_CREDENTIAL_FIELDS: (keyof AramexSettings)[] = ['accountNumber', 'accountPin', 'username', 'password']

@Component({
  selector: 'app-aramex-provider',
  templateUrl: './aramex-provider.component.html',
  styleUrls: ['../../../integrations/integrations.component.scss', '../shipping-providers-theme.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AramexProviderComponent implements OnInit, OnDestroy {
  @Input() projectId!: string
  @Input() isOpen = false
  @Output() openPanel = new EventEmitter<void>()
  @Output() closePanel = new EventEmitter<void>()
  @Output() saved = new EventEmitter<void>()

  settings: AramexSettings = {
    enabled: false,
    accountNumber: '',
    accountPin: '',
    accountEntity: '',
    accountCountryCode: '',
    username: '',
    password: '',
    originCountryCode: '',
    originCityName: '',
    originPostalCode: '',
    originAddressLine: '',
    originPhone: '',
  }

  // Aramex is only wired up for Saudi Arabia accounts today (see
  // docs/shipping/ARAMEX_TEST_DATA.md — both UAT accounts use AccountCountryCode/
  // OriginCountryCode 'SA'), so the country pickers only offer that option.
  countries = getCountryOptions(['SA'])

  originCities: string[] = []

  private statusSub?: Subscription

  constructor(public readonly service: AramexSettingsService) {}

  ngOnInit(): void {
    this.statusSub = this.service.status$.subscribe((status) => {
      if (!status?.connected) return
      this.settings.enabled = status.enabled
      this.settings.originCountryCode = status.originCountryCode ?? this.settings.originCountryCode
      this.settings.originCityName = status.originCityName ?? this.settings.originCityName
      this.settings.originPostalCode = status.originPostalCode ?? this.settings.originPostalCode
      this.settings.originAddressLine = status.originAddressLine ?? this.settings.originAddressLine
      this.settings.originPhone = status.originPhone ?? this.settings.originPhone
      this.settings.accountEntity = status.accountEntity ?? this.settings.accountEntity
      this.settings.accountCountryCode = status.accountCountryCode ?? this.settings.accountCountryCode
      this.service.maskCredentials(this.settings, ARAMEX_CREDENTIAL_FIELDS)
      this.refreshOriginCities()
    })
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe()
  }

  /**
   * Repopulates the Origin City options for the currently selected origin
   * country. Keeps an already-saved city selected even if it's spelled
   * differently than the reference list, so switching countries never
   * silently drops a previously saved value.
   */
  onOriginCountryChange(): void {
    this.settings.originCityName = ''
    this.refreshOriginCities()
  }

  private refreshOriginCities(): void {
    const countryIsoCode = this.settings.originCountryCode
    const curated = countryIsoCode === 'SA' ? MAJOR_SA_CITIES : undefined
    this.originCities = getCityOptions(countryIsoCode, this.settings.originCityName, curated)
  }

  /**
   * Saves encrypted Aramex configuration through stacks-agents.
   * Strips placeholder values so the backend preserves existing credentials.
   */
  async save(): Promise<void> {
    const settingsToSave = { ...this.settings }
    for (const field of ARAMEX_CREDENTIAL_FIELDS) {
      if (settingsToSave[field] === this.service.CREDENTIAL_PLACEHOLDER) {
        ;(settingsToSave as Record<string, unknown>)[field] = ''
      }
    }
    await this.service.save(this.projectId, settingsToSave)
    this.saved.emit()
  }

  /**
   * Disables Aramex through stacks-agents while keeping credential
   * handling inside the backend.
   */
  disconnect(): void {
    this.service.disconnect(this.projectId)
  }
}
